import S from './Footer.styled';
import React, { useEffect } from 'react';
import { Flex, MainInner } from '@/styles/global';
import { BreadNormal, H2, H5, H6 } from '@/styles/typography';
import { IButtonProps } from '@/components/storyblok/Button';
import NavLink, { ILinkBlok } from '@/components/storyblok/NavLink';
import { Icon, Image, StyledButton, Underline } from '@/components/global';
import { IAsset, ILink, IProduct } from '@/src/types';
import useProductsData from '@/src/data/useProductsData';
import { parseData } from '@/src/helpers';
import { Link } from 'gatsby';
import { storyblokEditable } from 'gatsby-source-storyblok';
import { modifyImage } from '@/src/helpers/modifyImage';
import { useSiteSettings } from '@/src/context/SiteSettingsContext';
import Form, { IForm } from '@/components/storyblok/Form';

interface IFooterProps {
  settings: {
    footer_bold_title: string;
    footer_regular_title: string;
    footer_buttons: IButtonProps[];
    footer_legal_links: ILinkBlok[];
    footer_our_boats_link: ILinkBlok[];
    footer_some_links: {
      icon: IAsset;
      link: ILink;
    }[];
    footer_logo: IAsset;
    footer_link_columns: {
      column_title: string;
      links: ILinkBlok[];
    }[];
    form: IForm[];
  };
}

const Footer: React.FC<IFooterProps> = ({ settings }) => {
  const {
    footer_bold_title,
    footer_regular_title,
    footer_buttons,
    footer_legal_links,
    footer_our_boats_link,
    footer_some_links,
    footer_logo,
    footer_link_columns,
  } = settings;

  const { slug } = useSiteSettings();
  const [isOpen, setIsOpen] = React.useState<null | number>(null);
  const [isMobile, setIsMobile] = React.useState<boolean>(false);
  const data: IProduct[] = useProductsData(slug);
  const parsedObjects: IProduct[] = parseData(data);

  const [formSuccess, setFormSuccess] = React.useState(false);

  const handleIndex = (index: number) => {
    if (!isMobile) return;
    if (isOpen === index) {
      setIsOpen(null);
    } else {
      setIsOpen(index);
    }
  };

  // Check window size when resizing
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 820);
    };

    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const renderColumns = footer_link_columns?.map(
    ({ column_title, links }, index) => {
      return (
        <S.AccordionWrapper key={index}>
          <S.AccordionHeader onClick={() => handleIndex(index)}>
            <H6 $weight={400}>{column_title}</H6>
            {isMobile && isOpen === index ? (
              <Icon src={'Minus'} />
            ) : (
              <Icon src={'Plus'} />
            )}
          </S.AccordionHeader>
          {isOpen === index && (
            <S.AccordionContent>
              {links?.map((blok, index) => {
                return (
                  <NavLink
                    key={index}
                    size="14px"
                    blok={{
                      label: blok.label,
                      link: blok.link,
                    }}
                  />
                );
              })}
            </S.AccordionContent>
          )}
          {!isMobile && (
            <S.AccordionContent>
              {links?.map((blok, index) => {
                return (
                  <NavLink
                    key={index}
                    blok={{
                      label: blok.label,
                      link: blok.link,
                    }}
                  />
                );
              })}
            </S.AccordionContent>
          )}
        </S.AccordionWrapper>
      );
    }
  );

  const modLogo = modifyImage(footer_logo, '300x60');

  return (
    <>
      <Underline color={'lineGrey'} />
      <MainInner>
        <S.Footer {...storyblokEditable(settings)}>
          <S.FooterFlex>
            <Flex $column $width={'100%'} $desktopWidth={'fit-content'}>
              <H2 $weight={400}>{footer_bold_title}</H2>
              <H2>{footer_regular_title}</H2>
              <Flex
                $column
                $desktopDir={'row'}
                $gap={16}
                $margin="22px 0"
                $width={'100%'}
              >
                {footer_buttons?.map((blok, index) => (
                  <StyledButton {...blok} mobileFullWidth key={index} />
                ))}
              </Flex>
              {settings && settings.form && settings.form[0] && (
                <S.FormWrapper>
                  {formSuccess ? (
                    <H5 $weight={325} $maxWidth={390}>
                      Thank you for subscribing to our newsletter! We're excited
                      to welcome you to our community.
                    </H5>
                  ) : (
                    <Form
                      blok={settings.form[0]}
                      noTermsTitle
                      wrap={false}
                      onSuccess={() => setFormSuccess(true)}
                    />
                  )}
                </S.FormWrapper>
              )}
            </Flex>

            <S.ColumnsWrapper $wrap>
              <Flex
                $column
                $gap={14}
                $margin={'30px 0 46px 0'}
                $marginDesktop={'0'}
              >
                <H6 $weight={400}>Our Boats</H6>
                {parsedObjects?.map((product: IProduct, i) => {
                  return (
                    <S.Product
                      href={`
                    /${product.full_slug}
                  `}
                      key={i}
                    >
                      <Image
                        image={product.content.image}
                        height={60}
                        width={95}
                        size="200x115"
                      />
                      <H6 $weight={400}>{product.name}</H6>
                    </S.Product>
                  );
                })}
                {footer_our_boats_link?.map((blok, index) => {
                  return (
                    <NavLink
                      key={index}
                      size="14px"
                      blok={{
                        label: blok.label,
                        link: blok.link,
                      }}
                    />
                  );
                })}
              </Flex>
              <S.ColumnsWrapper>{renderColumns}</S.ColumnsWrapper>
            </S.ColumnsWrapper>
          </S.FooterFlex>

          <Flex
            $column
            $gap={70}
            $margin={'32px 0'}
            $desktopDir={'row-reverse'}
            $justify={'between'}
            $marginDesktop={'48px 0 18px 0'}
          >
            <Flex $gap={12}>
              {footer_some_links?.map((blok, index) => {
                return (
                  <S.SomeLink
                    target="_blank"
                    href={blok.link?.url}
                    key={index}
                    aria-label="Social Media"
                  >
                    <S.SomeImageWrapper>
                      <Image image={blok.icon} width={30} height={30} />
                    </S.SomeImageWrapper>
                  </S.SomeLink>
                );
              })}
            </Flex>
            <Flex>
              {footer_logo && (
                <Link to={slug ? slug : '/'} aria-label="Home">
                  <S.Logo
                    src={modLogo}
                    alt={footer_logo.alt}
                    width={180}
                    height={36}
                  />
                </Link>
              )}
            </Flex>
          </Flex>

          <S.Line />

          <Flex
            $column
            $gap={16}
            $width={'100%'}
            $desktopDir="row"
            $justify={'space-between'}
            $margin={'8px 0 40px 0'}
            $marginDesktop={'18px 0 40px 0'}
          >
            <Flex $gap={16} $wrap="wrap">
              {/* <NavLink
                onClick={openBanner}
                size="12px"
                blok={{
                  label: 'Cookie Settings',
                }}
              /> */}
              <button id="ot-sdk-btn" className="ot-sdk-show-settings">
                Cookie Settings
              </button>
              {footer_legal_links?.map((blok, index) => (
                <NavLink
                  key={index}
                  size="12px"
                  blok={{
                    label: blok.label,
                    link: blok.link,
                  }}
                />
              ))}
            </Flex>
            {/* <BreadNormal $margin={'0 0 0 auto'}>ENG</BreadNormal> */}
            <Flex $margin={'0 0 0 auto'} $align={'center'} $gap={5}>
              <S.LangLink
                to="/"
                aria-label="Change language to English"
                $active={!slug}
              >
                ENG
              </S.LangLink>
              <BreadNormal $color="lineGrey">|</BreadNormal>
              <S.LangLink
                to="/us"
                aria-label="Change language to English"
                $active={slug === '/us'}
              >
                US
              </S.LangLink>
              <BreadNormal $color="lineGrey">|</BreadNormal>
              <S.LangLink
                to="/de"
                aria-label="Change language to German"
                $active={slug === '/de'}
              >
                GER
              </S.LangLink>
            </Flex>
          </Flex>
        </S.Footer>
      </MainInner>
    </>
  );
};

export default Footer;
